import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext.jsx';
import RequireAuth from './auth/RequireAuth.jsx';
import Layout from './components/Layout.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { ToastHost } from './components/Toasts.jsx';
import CommandPalette from './components/CommandPalette.jsx';
import LoginPage from './pages/LoginPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import DigestPage from './pages/DigestPage.jsx';
import ApplicationsPage from './pages/ApplicationsPage.jsx';
import MailPage from './pages/MailPage.jsx';
import TasksPage from './pages/TasksPage.jsx';
import AnalyticsPage from './pages/AnalyticsPage.jsx';
import SourcesPage from './pages/SourcesPage.jsx';
import DocumentsPage from './pages/DocumentsPage.jsx';
import ProfilePage from './pages/ProfilePage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import OnboardingPage from './pages/OnboardingPage.jsx';

// Pre-auth routes bypass <Layout>; everything else renders inside it.
// T099 — the per-page boundary sits INSIDE <Layout> so a crash in one
// page keeps the sidebar alive and the operator can navigate away.
// key={section} is load-bearing: every route renders this same tree
// shape, so without a distinct key React reconciles the boundary as ONE
// instance across navigation and a caught error sticks to every page.
// A new key forces a remount (fresh error state) per route.
const protectedRoute = (element, section) => (
  <RequireAuth>
    <Layout>
      <ErrorBoundary key={section} section={section}>{element}</ErrorBoundary>
    </Layout>
  </RequireAuth>
);

export default function App() {
  return (
    <AuthProvider>
      {/* T099 — top-level boundary catches Layout/auth/palette render
          errors; without it any render throw unmounts the whole app. */}
      <ErrorBoundary section="hunter">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* /onboarding renders bare — no sidebar/layout — so the wizard
              owns the full viewport on first login. Still auth-gated. */}
          <Route
            path="/onboarding"
            element={
              <RequireAuth>
                <ErrorBoundary section="Onboarding">
                  <OnboardingPage />
                </ErrorBoundary>
              </RequireAuth>
            }
          />
          <Route path="/" element={protectedRoute(<DashboardPage />, 'Dashboard')} />
          <Route path="/digest" element={protectedRoute(<DigestPage />, 'Digest')} />
          <Route path="/applications" element={protectedRoute(<ApplicationsPage />, 'Applications')} />
          <Route path="/tasks" element={protectedRoute(<TasksPage />, 'Tasks')} />
          <Route path="/mail" element={protectedRoute(<MailPage />, 'Unlinked mail')} />
          <Route path="/analytics" element={protectedRoute(<AnalyticsPage />, 'Analytics')} />
          <Route path="/sources" element={protectedRoute(<SourcesPage />, 'Sources')} />
          <Route path="/profile" element={protectedRoute(<ProfilePage />, 'Profile')} />
          <Route path="/documents" element={protectedRoute(<DocumentsPage />, 'Documents')} />
          <Route path="/settings" element={protectedRoute(<SettingsPage />, 'Settings')} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ToastHost />
        <CommandPalette />
      </ErrorBoundary>
    </AuthProvider>
  );
}
